import { useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { PageHeader } from "@/components/common/PageHeader";
import { FButton } from "@/components/common/FButton";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";
import { submitCreatorApplication, submitAmbassadorApplication } from "@/lib/api";

const inputCls =
  "border-white/15 bg-ff-midnight text-ff-ivory placeholder:text-ff-grey/50 focus-visible:ring-ff-ember focus-visible:ring-offset-0";
const labelCls = "text-sm font-medium text-ff-grey";

const Field = ({ label, htmlFor, children, required }) => (
  <div className="space-y-2">
    <Label htmlFor={htmlFor} className={labelCls}>
      {label} {required && <span className="text-ff-ember">*</span>}
    </Label>
    {children}
  </div>
);

const SuccessCard = ({ reference, message, onReset, testid }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    data-testid={testid}
    className="rounded-3xl border border-ff-teal/25 bg-ff-teal/[0.06] p-8 text-center md:p-12"
  >
    <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-ff-teal/15 text-ff-teal">
      <CheckCircle2 className="h-8 w-8" />
    </span>
    <h3 className="mt-6 font-display text-2xl font-bold text-ff-ivory">Application received</h3>
    <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-ff-grey">{message}</p>
    {reference && (
      <div className="mt-6 inline-flex items-center gap-2">
        <StatusBadge tone="positive">Reference</StatusBadge>
        <span className="font-mono text-lg font-semibold text-ff-ivory">{reference}</span>
      </div>
    )}
    <div className="mt-8">
      <button
        onClick={onReset}
        data-testid="submit-another-btn"
        className="text-sm font-medium text-ff-ember hover:text-ff-ivory"
      >
        Submit another application
      </button>
    </div>
  </motion.div>
);

function CreatorForm() {
  const empty = {
    stage_name: "", email: "", telegram: "", country: "",
    main_handle: "", has_paid_platform: "no", which_platform: "", audience_platform: "",
    audience_size: "", help_needed: "", preferred_payout: "", referral_code: "",
    consent_no_passwords: false, consent_ai_drafts: false, consent_fees: false,
  };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.stage_name.trim() || !form.email.trim() || !form.telegram.trim() || !form.country.trim() || !form.main_handle.trim()) {
      toast.error("Please fill in the required fields.");
      return;
    }
    if (!form.audience_platform || !form.audience_size || !form.help_needed || !form.preferred_payout) {
      toast.error("Please complete the platform and support details.");
      return;
    }
    if (!form.consent_no_passwords || !form.consent_ai_drafts || !form.consent_fees) {
      toast.error("Please confirm all three consent statements.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await submitCreatorApplication(form);
      setResult(data);
      toast.success("Application submitted");
    } catch (err) {
      const detail = err.errors ? ` ${Object.values(err.errors)[0]}` : "";
      toast.error(`${err.message || "Something went wrong. Please try again."}${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <SuccessCard
        reference={result.reference}
        message={result.message}
        onReset={() => { setForm(empty); setResult(null); }}
        testid="creator-success"
      />
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="creator-form" noValidate>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Stage name" htmlFor="stage_name" required>
          <Input id="stage_name" data-testid="creator-stage-name" className={inputCls} value={form.stage_name} onChange={(e) => set("stage_name", e.target.value)} placeholder="How you're known" />
        </Field>
        <Field label="Email" htmlFor="email" required>
          <Input id="email" type="email" data-testid="creator-email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
        </Field>
        <Field label="Telegram username" htmlFor="telegram" required>
          <Input id="telegram" data-testid="creator-telegram" className={inputCls} value={form.telegram} onChange={(e) => set("telegram", e.target.value)} placeholder="@yourhandle" />
        </Field>
        <Field label="Country" htmlFor="country" required>
          <Input id="country" data-testid="creator-country" className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="e.g. Jamaica" />
        </Field>
        <Field label="Main platform or social handle" htmlFor="main_handle" required>
          <Input id="main_handle" data-testid="creator-main-handle" className={inputCls} value={form.main_handle} onChange={(e) => set("main_handle", e.target.value)} placeholder="@handle or link" />
        </Field>
      </div>

      <Field label="Do you already have a paid platform?" required>
        <RadioGroup
          value={form.has_paid_platform}
          onValueChange={(v) => set("has_paid_platform", v)}
          className="flex gap-6"
          data-testid="creator-has-paid"
        >
          {["yes", "no"].map((v) => (
            <div key={v} className="flex items-center gap-2">
              <RadioGroupItem value={v} id={`paid-${v}`} className="border-ff-grey/40 text-ff-ember" />
              <Label htmlFor={`paid-${v}`} className="capitalize text-ff-ivory">{v}</Label>
            </div>
          ))}
        </RadioGroup>
      </Field>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Which platform?">
          <Select value={form.which_platform} onValueChange={(v) => set("which_platform", v)}>
            <SelectTrigger data-testid="creator-which-platform" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="border-white/10 bg-ff-slate text-ff-ivory">
              {["OnlyFans", "Fansly", "Other", "Not yet"].map((o) => (
                <SelectItem key={o} value={o} className="focus:bg-white/5 focus:text-ff-ivory">{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Main audience platform" required>
          <Select value={form.audience_platform} onValueChange={(v) => set("audience_platform", v)}>
            <SelectTrigger data-testid="creator-audience-platform" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="border-white/10 bg-ff-slate text-ff-ivory">
              {["X / Twitter", "Instagram", "TikTok", "Telegram", "Other"].map((o) => (
                <SelectItem key={o} value={o} className="focus:bg-white/5 focus:text-ff-ivory">{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Approximate audience size" required>
          <Select value={form.audience_size} onValueChange={(v) => set("audience_size", v)}>
            <SelectTrigger data-testid="creator-audience-size" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="border-white/10 bg-ff-slate text-ff-ivory">
              {["Under 1K", "1K – 10K", "10K – 50K", "50K – 250K", "250K+"].map((o) => (
                <SelectItem key={o} value={o} className="focus:bg-white/5 focus:text-ff-ivory">{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Preferred payout method" required>
          <Select value={form.preferred_payout} onValueChange={(v) => set("preferred_payout", v)}>
            <SelectTrigger data-testid="creator-payout" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="border-white/10 bg-ff-slate text-ff-ivory">
              {["Western Union", "RIA", "MoneyGram", "PayPal", "Bank transfer (later)"].map((o) => (
                <SelectItem key={o} value={o} className="focus:bg-white/5 focus:text-ff-ivory">{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label="What do you need help with?" required>
        <Select value={form.help_needed} onValueChange={(v) => set("help_needed", v)}>
          <SelectTrigger data-testid="creator-help" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent className="border-white/10 bg-ff-slate text-ff-ivory">
            {["Launch", "Content", "Cashout", "Growth", "Privacy", "All of it"].map((o) => (
              <SelectItem key={o} value={o} className="focus:bg-white/5 focus:text-ff-ivory">{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field label="Referral or ambassador code (optional)" htmlFor="referral_code">
        <Input id="referral_code" data-testid="creator-referral" className={inputCls} value={form.referral_code} onChange={(e) => set("referral_code", e.target.value)} placeholder="e.g. AMB-1234" />
      </Field>

      <div className="space-y-4 rounded-2xl border border-white/10 bg-ff-midnight/60 p-6">
        {[
          ["consent_no_passwords", "I understand FanFuel does not ask for platform passwords."],
          ["consent_ai_drafts", "I understand AI outputs are drafts and I choose what to post."],
          ["consent_fees", "I understand FanFuel has no upfront fees and earns when creators earn."],
        ].map(([key, text]) => (
          <label key={key} className="flex cursor-pointer items-start gap-3">
            <Checkbox
              checked={form[key]}
              onCheckedChange={(v) => set(key, !!v)}
              data-testid={`creator-${key.replace(/_/g, "-")}`}
              className="mt-0.5 border-ff-grey/40 data-[state=checked]:border-ff-ember data-[state=checked]:bg-ff-ember data-[state=checked]:text-ff-midnight"
            />
            <span className="text-sm leading-relaxed text-ff-grey">{text}</span>
          </label>
        ))}
      </div>

      <FButton type="submit" disabled={submitting} data-testid="creator-submit" className="w-full sm:w-auto">
        {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <>Apply to FanFuel <ArrowRight className="h-4 w-4" /></>}
      </FButton>
    </form>
  );
}

function AmbassadorForm() {
  const empty = {
    name: "", email: "", telegram: "", country: "", social_handle: "",
    referral_plan: "", knows_creators: "", consent_eligibility: false,
  };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.telegram.trim() || !form.country.trim() || !form.social_handle.trim() || !form.referral_plan.trim()) {
      toast.error("Please fill in the required fields.");
      return;
    }
    if (!form.consent_eligibility) {
      toast.error("Please confirm the eligibility statement.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await submitAmbassadorApplication(form);
      setResult(data);
      toast.success("Application submitted");
    } catch (err) {
      const detail = err.errors ? ` ${Object.values(err.errors)[0]}` : "";
      toast.error(`${err.message || "Something went wrong. Please try again."}${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <SuccessCard
        reference={result.reference}
        message={result.message}
        onReset={() => { setForm(empty); setResult(null); }}
        testid="ambassador-success"
      />
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="ambassador-form" noValidate>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Name or display name" htmlFor="amb_name" required>
          <Input id="amb_name" data-testid="amb-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
        </Field>
        <Field label="Email" htmlFor="amb_email" required>
          <Input id="amb_email" type="email" data-testid="amb-email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
        </Field>
        <Field label="Telegram username" htmlFor="amb_telegram" required>
          <Input id="amb_telegram" data-testid="amb-telegram" className={inputCls} value={form.telegram} onChange={(e) => set("telegram", e.target.value)} placeholder="@yourhandle" />
        </Field>
        <Field label="Country" htmlFor="amb_country" required>
          <Input id="amb_country" data-testid="amb-country" className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="e.g. Trinidad" />
        </Field>
      </div>

      <Field label="Social handle" htmlFor="amb_social" required>
        <Input id="amb_social" data-testid="amb-social" className={inputCls} value={form.social_handle} onChange={(e) => set("social_handle", e.target.value)} placeholder="@handle or link" />
      </Field>

      <Field label="How do you plan to refer creators?" htmlFor="amb_plan" required>
        <Textarea id="amb_plan" data-testid="amb-plan" className={`${inputCls} min-h-[110px]`} value={form.referral_plan} onChange={(e) => set("referral_plan", e.target.value)} placeholder="Tell us about your reach and approach…" />
      </Field>

      <Field label="Do you already know creators who may be a fit?" htmlFor="amb_knows">
        <Textarea id="amb_knows" data-testid="amb-knows" className={`${inputCls} min-h-[90px]`} value={form.knows_creators} onChange={(e) => set("knows_creators", e.target.value)} placeholder="Optional — no names needed yet" />
      </Field>

      <div className="rounded-2xl border border-white/10 bg-ff-midnight/60 p-6">
        <label className="flex cursor-pointer items-start gap-3">
          <Checkbox
            checked={form.consent_eligibility}
            onCheckedChange={(v) => set("consent_eligibility", !!v)}
            data-testid="amb-consent"
            className="mt-0.5 border-ff-grey/40 data-[state=checked]:border-ff-ember data-[state=checked]:bg-ff-ember data-[state=checked]:text-ff-midnight"
          />
          <span className="text-sm leading-relaxed text-ff-grey">
            I understand commissions depend on verified eligible referrals.
          </span>
        </label>
      </div>

      <FButton type="submit" disabled={submitting} data-testid="ambassador-submit" className="w-full sm:w-auto">
        {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <>Apply as Ambassador <ArrowRight className="h-4 w-4" /></>}
      </FButton>
    </form>
  );
}

export default function Apply() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const defaultTab = params.get("type") === "ambassador" ? "ambassador" : "creator";

  return (
    <div data-testid="apply-page">
      <PageHeader
        overline="Apply / Join"
        titleLines={["Start your", "FanFuel journey."]}
        lead="Tell us about you. We review every application for fit and reach out via Telegram or email. No upfront fees, no passwords."
        testid="apply-header"
      />

      <section className="px-5 pb-20 md:px-8 md:pb-28">
        <div className="mx-auto max-w-3xl">
          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 rounded-full border border-white/10 bg-ff-slate/60 p-1" data-testid="apply-tabs">
              <TabsTrigger
                value="creator"
                data-testid="tab-creator"
                className="rounded-full text-sm font-semibold text-ff-grey data-[state=active]:bg-ff-ember data-[state=active]:text-ff-midnight"
              >
                Creator Application
              </TabsTrigger>
              <TabsTrigger
                value="ambassador"
                data-testid="tab-ambassador"
                className="rounded-full text-sm font-semibold text-ff-grey data-[state=active]:bg-ff-ember data-[state=active]:text-ff-midnight"
              >
                Ambassador Application
              </TabsTrigger>
            </TabsList>

            <TabsContent value="creator" className="mt-10">
              <CreatorForm />
            </TabsContent>
            <TabsContent value="ambassador" className="mt-10">
              <AmbassadorForm />
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
